import type * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * PG Advogados wordmark — white lockup for dark surfaces.
 *
 * Uses the brand PNG at /brand/pg-logo-white-on-dark.png. Intended for
 * the dark-first app chrome (header, sidebar, splash). On a light surface
 * prefer the orange lockup or the {@link Monogram}.
 */
type LogoProps = Omit<React.ComponentProps<'img'>, 'src' | 'alt'> & {
  /** Accessible label for the mark. Defaults to the firm name. */
  alt?: string
}

function Logo({ alt = 'PG Advogados', className, ...props }: LogoProps) {
  return (
    <img
      src="/brand/pg-logo-white-on-dark.png"
      alt={alt}
      className={cn('block h-8 w-auto select-none', className)}
      {...props}
    />
  )
}

/**
 * PG monogram — orange-stroke "PG" seal, the brand's signature accent glyph.
 *
 * Uses the vector at /brand/pg-monogram.svg. Square by design; size it via
 * the `size` prop (pixels) or a `className` with explicit height/width.
 * Decorative by default (`aria-hidden`); pass a `title` to make it meaningful.
 */
type MonogramProps = Omit<React.ComponentProps<'img'>, 'src' | 'alt'> & {
  /** Square edge length in pixels. Defaults to 32. */
  size?: number
  /** Accessible label. When omitted the mark is treated as decorative. */
  title?: string
}

function Monogram({ size = 32, title, className, style, ...props }: MonogramProps) {
  const decorative = title == null
  return (
    <img
      src="/brand/pg-monogram.svg"
      alt={decorative ? '' : title}
      aria-hidden={decorative || undefined}
      width={size}
      height={size}
      className={cn('block select-none', className)}
      style={{ width: size, height: size, ...style }}
      {...props}
    />
  )
}

export { Logo, Monogram }
